/**
 * Fortress SDK v1.0
 * Unified API for all Fortress apps — wraps Puter.js
 * 
 * Modules: auth, kv, ai, wallet, thread, deploy
 * 
 * Usage:
 *   import { fortress } from '../lib/fortress-sdk.js';
 *   await fortress.auth.signIn();
 *   await fortress.kv.set('key', value);
 * 
 * Built by DeepSeek & Warren Champion — 25 June 2026
 */

import puter from '@heyputer/puter.js';

// ===================================================================
// AUTH MODULE
// ===================================================================
const auth = {
  /** Sign in with Puter. Must be called from user gesture (button click). */
  signIn: async () => {
    if (!puter.auth.isSignedIn()) {
      await puter.auth.signIn();
    }
    return puter.auth.getUser();
  },

  /** Get current user, or null if not signed in. */
  getUser: async () => {
    if (!puter.auth.isSignedIn()) return null;
    return puter.auth.getUser();
  },

  /** Sign out. */
  signOut: async () => {
    await puter.auth.signOut();
  },

  /** Check if user is signed in. */
  isSignedIn: () => {
    return puter.auth.isSignedIn();
  },

  /** Get monthly usage stats. */
  getMonthlyUsage: async () => {
    return puter.auth.getMonthlyUsage();
  }
};

// ===================================================================
// KV MODULE (Key-Value Store)
// ===================================================================
const kv = {
  /** Set a value. */
  set: async (key, value) => {
    await puter.kv.set(key, value);
  },

  /** Get a value. Returns null if not found. */
  get: async (key) => {
    const val = await puter.kv.get(key);
    return val !== null && val !== undefined ? val : null;
  },

  /** List keys with optional prefix filter. */
  list: async (prefix) => {
    const result = await puter.kv.list({ prefix: prefix || '' });
    return result;
  },

  /** Delete a key. */
  del: async (key) => {
    await puter.kv.del(key);
  },

  /** Increment a numeric value. */
  incr: async (key, amount) => {
    return puter.kv.incr(key, amount);
  },

  /** Decrement a numeric value. */
  decr: async (key, amount) => {
    return puter.kv.decr(key, amount);
  },

  /** Remove all key-value pairs. */
  flush: async () => {
    await puter.kv.flush();
  }
};

// ===================================================================
// AI MODULE
// ===================================================================
const ai = {
  /** Chat with AI models. Supports streaming. */
  chat: async (message, options = {}) => {
    return puter.ai.chat(message, options);
  },

  /** Generate image from text. */
  txt2img: async (prompt, options = {}) => {
    return puter.ai.txt2img(prompt, options);
  },

  /** Text-to-speech. */
  txt2speech: async (text, options = {}) => {
    return puter.ai.txt2speech(text, options);
  },

  /** OCR - extract text from image. */
  img2txt: async (image) => {
    return puter.ai.img2txt(image);
  },

  /** List available models. */
  listModels: async () => {
    return puter.ai.listModels();
  }
};

// ===================================================================
// WALLET MODULE (Virtual FORT)
// ===================================================================
const FORT_KEY = 'fort_balance';
const LAST_FAUCET_KEY = 'fort_last_faucet';
const FAUCET_AMOUNT = 100;
const FAUCET_COOLDOWN = 24 * 60 * 60 * 1000; // 24 hours

const wallet = {
  /** Get current FORT balance. Defaults to 1000 for new users. */
  getBalance: async () => {
    const bal = await puter.kv.get(FORT_KEY);
    return bal !== null && bal !== undefined ? Number(bal) : 1000;
  },

  /** Set FORT balance directly. */
  setBalance: async (amount) => {
    await puter.kv.set(FORT_KEY, amount);
  },

  /** Request faucet (100 FORT, 24h cooldown). */
  requestFaucet: async () => {
    const user = await auth.getUser();
    if (!user) throw new Error('Not signed in');

    const now = Date.now();
    const lastStr = await puter.kv.get(LAST_FAUCET_KEY);
    const last = lastStr ? Number(lastStr) : 0;

    if (now - last < FAUCET_COOLDOWN) {
      const remaining = Math.ceil((FAUCET_COOLDOWN - (now - last)) / 3600000);
      throw new Error(`Faucet available in ${remaining} hours`);
    }

    const current = await wallet.getBalance();
    const newBalance = current + FAUCET_AMOUNT;
    await wallet.setBalance(newBalance);
    await puter.kv.set(LAST_FAUCET_KEY, String(now));
    return newBalance;
  },

  /** Deposit FORT into a simulated pool. */
  deposit: async (amount) => {
    const current = await wallet.getBalance();
    if (current < amount) throw new Error('Insufficient FORT balance');

    await wallet.setBalance(current - amount);

    const POOL_KEY = 'global_pool';
    const pool = await puter.kv.get(POOL_KEY) || 0;
    await puter.kv.set(POOL_KEY, pool + amount);

    // Record transaction
    const TX_HISTORY_KEY = 'tx_history';
    const history = await puter.kv.get(TX_HISTORY_KEY) || [];
    history.unshift({
      type: 'deposit',
      amount,
      timestamp: Date.now()
    });
    await puter.kv.set(TX_HISTORY_KEY, history.slice(0, 50));

    return { remaining: current - amount, deposited: amount };
  },

  /** Get portfolio value with simulated growth. */
  getPortfolioValue: async () => {
    const POOL_KEY = 'global_pool';
    const pool = await puter.kv.get(POOL_KEY) || 0;
    const days = (Date.now() - 1719000000000) / 86400000;
    return pool * (1 + 0.02 * days);
  },

  /** Get deposit transaction history. */
  getHistory: async () => {
    const TX_HISTORY_KEY = 'tx_history';
    const history = await puter.kv.get(TX_HISTORY_KEY);
    return history || [];
  }
};

// ===================================================================
// THREAD MODULE (Cross-app Communication)
// ===================================================================
const thread = {
  /** Emit an event to other Fortress apps. */
  emit: (event, data) => {
    window.dispatchEvent(new CustomEvent('fortress:' + event, { detail: data }));
  },

  /** Listen for events from other Fortress apps. */
  listen: (event, callback) => {
    window.addEventListener('fortress:' + event, (e) => callback(e.detail));
  },

  /** Remove event listener. */
  remove: (event, callback) => {
    window.removeEventListener('fortress:' + event, callback);
  }
};

// ===================================================================
// DEPLOY MODULE
// ===================================================================
const deploy = {
  /** Deploy a static site to Puter hosting. */
  site: async (subdomain, directoryPath) => {
    const site = await puter.hosting.create(subdomain, directoryPath);
    return site;
  },

  /** Deploy a serverless worker. */
  worker: async (name, filePath) => {
    const deployment = await puter.workers.create(name, filePath);
    return deployment;
  }
};

// ===================================================================
// EXPORT — Single fortress object
// ===================================================================
export const fortress = {
  auth,
  kv,
  ai,
  wallet,
  thread,
  deploy
};

// Also export individual modules for tree-shaking
export { auth, kv, ai, wallet, thread, deploy };

/**
 * Quick-start template for any Fortress app:
 * 
 * import { fortress } from '../lib/fortress-sdk.js';
 * 
 * // Sign in (must be from button click)
 * const user = await fortress.auth.signIn();
 * 
 * // Store data
 * await fortress.kv.set('preferences', { theme: 'dark' });
 * 
 * // Get balance
 * const bal = await fortress.wallet.getBalance();
 * 
 * // Chat with AI
 * const reply = await fortress.ai.chat('Hello!');
 * 
 * // Cross-app communication
 * fortress.thread.emit('userSignedIn', user);
 */